<template>
  <div
    class="tracking-wide overflow-hidden"
    :class="{
      responsive: responsive,
      'border-2 border-gray-200 rounded-8': responsive || outlined,
    }"
  >
    <div class="flex flex-col" :class="{ 'md:flex-row md:relative': responsive }">
      <div
        class="highlight flex justify-center items-center h-36"
        :class="[
          type,
          { 'md:w-44 md:h-full md:absolute md:mb-0': responsive, 'mb-6': !slotMargin },
        ]"
      >
        <img
          class="icon icon-xl"
          :class="{ 'md:h-20 md:w-20': responsive }"
          :src="icon"
          :alt="type"
        />
      </div>
      <div
        class="px-6"
        :class="[{ 'md:my-14 md:px-10 md:ml-44': responsive, 'mb-8': !slotMargin }, slotMargin]"
      >
        <slot />
      </div>
    </div>
    <SimpleCta
      v-if="primaryLink"
      :primary="primaryLink"
      :secondary="secondaryLink"
      class="cta py-6 mt-2"
      :class="[{ 'px-box-4 md:bg-gray-50': responsive }, { 'mx-8': !responsive }]"
    />
  </div>
</template>

<script lang="ts" setup>
import type { HighlightBoxSliceType } from '@core/types/generated'
import DangerIcon from './icons/danger.svg'
import WarningIcon from './icons/warning.svg'
import InfoIcon from './icons/info.svg'
import RecommendationIcon from './icons/recommendation.svg'

const props = withDefaults(
  defineProps<{
    responsive?: boolean
    outlined?: boolean
    primaryLink?: { label: string | null; link: string | null } | null
    secondaryLink?: { label: string | null; link: string | null } | null
    slotMargin?: string
    type: HighlightBoxSliceType
  }>(),
  {
    type: 'info',
  },
)

const icon = computed(() => {
  switch (props.type) {
    case 'danger':
      return DangerIcon
    case 'warning':
      return WarningIcon
    case 'info':
      return InfoIcon
    case 'recommendation':
      return RecommendationIcon
    default:
      return undefined
  }
})
</script>

<style scoped>
.recommendation {
  @apply bg-green-500/25;
}

.info {
  @apply bg-blue-300/25;
}

.warning {
  @apply bg-amber-500/25;
}

.danger {
  @apply bg-red-600/25;
}

.highlight {
  clip-path: inset(0 -10% 0 -10% round 0 0 100% 100%);
}

@screen md {
  .responsive .highlight {
    clip-path: inset(-10% 0 -10% 0 round 0 100% 100% 0);
  }

  .responsive .cta {
    filter: drop-shadow(0 -2px 0 theme('colors.gray.200'));
  }

  .responsive .cta::before {
    content: '';
    @apply absolute bg-gray-50 w-full left-0 h-6 -top-6 mt-px;
    clip-path: polygon(0 0, 50% calc(100% - 2px), 100% 0%, 100% 100%, 0 100%);
  }
}
</style>
